import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { RootState } from '../../redux/store'; // Adjust path if needed
import { Box, CircularProgress } from '@mui/material';
import DashboardNav from './components/DashboardNav';
import DashboardBottomNav from './components/DashboardBottomNav';

const drawerWidth = 240;

const DashboardLayout: React.FC = () => {
  const {
    user,
    isAuthenticated,
    loading: userLoading,
  } = useSelector((state: RootState) => state.user);
  const location = useLocation();
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // Check if we're currently in the process of creating a user
  // This helps us stabilize the view during user creation to prevent remounts
  useEffect(() => {
    const checkUserCreation = () => {
      const createUserStartTime = sessionStorage.getItem('createUserStartTime');
      const isActiveUserCreation =
        createUserStartTime &&
        // If creation started less than 30 seconds ago, consider it active
        Date.now() - parseInt(createUserStartTime) < 30000;

      setIsCreatingUser(isActiveUserCreation || false);

      if (isActiveUserCreation) {
        console.log('[DashboardLayout] User creation in progress, stabilizing layout');
      }
    };

    // Check when the component mounts
    checkUserCreation();

    // Also check periodically to detect when creation is complete
    const interval = setInterval(checkUserCreation, 1000);
    return () => clearInterval(interval);
  }, []);

  console.log('[DashboardLayout] Mounted. Location:', location.pathname);

  useEffect(() => {
    console.log('[DashboardLayout] User State:', {
      isAuthenticated,
      userType: user?.userType,
      loading: userLoading,
    });
  }, [isAuthenticated, user, userLoading]);

  // If we're creating a user, bypass authentication checks to prevent remounting
  if (isCreatingUser) {
    console.log('[DashboardLayout] User creation active, bypassing auth checks');
    return (
      <Box sx={{ display: 'flex', height: '100%' }}>
        <DashboardNav drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pb: { xs: '72px', md: 3 },
            pt: 3,
            px: 3,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <Outlet />
        </Box>
        <DashboardBottomNav />
      </Box>
    );
  }

  if (userLoading) {
    console.log('[DashboardLayout] User loading...');
    // Render centered spinner within the main content area
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: 'calc(100vh - 128px)' }}
      >
        {' '}
        {/* Adjust height based on Header/Footer */}
        <CircularProgress />
      </Box>
    );
  }

  // Redirect if not authenticated or not an admin
  if (!isAuthenticated || user?.userType !== 'admin') {
    console.warn(
      '[DashboardLayout] Access Denied: Redirecting. Auth:',
      isAuthenticated,
      'Type:',
      user?.userType
    );
    return <Navigate to="/" replace />;
  }

  console.log('[DashboardLayout] Rendering layout with Nav and Outlet...');

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {' '}
      {/* Ensure layout takes height */}
      {/* No CssBaseline needed here, App.tsx has it */}
      {/* No AppBar needed here, App.tsx provides the standard Header */}
      <DashboardNav drawerWidth={drawerWidth} />
      {/* Main Dashboard Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pb: { xs: '72px', md: 3 },
          pt: 3,
          px: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: '100%',
          overflowY: 'auto',
        }}
      >
        <Outlet />
      </Box>
      <DashboardBottomNav />
    </Box>
  );
};

export default DashboardLayout;
